/**
 * Primary action (§13.4): verb-plus-outcome label, visible focus, disabled state explains why, loading state
 * preserves width and announces status. Render as <button> or (with `href`) as <a>.
 */
import {
  forwardRef,
  useId,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../cn';

export type ButtonVariant = 'primary' | 'prize' | 'secondary' | 'quiet' | 'sponsor' | 'danger';
export type ButtonSize = 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  /** Shows a spinner, keeps the label's width and sets aria-busy. Clicks are ignored while loading. */
  loading?: boolean;
  /** Text announced while loading (default "Working"). */
  loadingLabel?: string;
  /** Why the control is disabled; rendered as visible text under the button and linked via aria-describedby. */
  disabledReason?: string;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  className?: string;
  children: ReactNode;
}

export type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { href?: undefined };
export type LinkButtonProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & {
    href: string;
    disabled?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps | LinkButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      block = false,
      loading = false,
      loadingLabel = 'Working',
      disabledReason,
      icon,
      iconEnd,
      className,
      children,
      disabled,
      ...rest
    } = props;
    const reasonId = useId();
    const isDisabled = Boolean(disabled) || loading;
    const shared = {
      className: cn('wd-btn', className),
      'data-variant': variant,
      'data-size': size,
      'data-block': block ? 'true' : undefined,
      'data-loading': loading ? 'true' : undefined,
      'aria-disabled': isDisabled ? true : undefined,
      'aria-busy': loading ? true : undefined,
      'aria-describedby': disabledReason && disabled ? reasonId : undefined,
    } as const;
    const body = (
      <>
        {icon ? (
          <span className="wd-btn__label" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="wd-btn__label">{children}</span>
        {iconEnd ? (
          <span className="wd-btn__label" aria-hidden="true">
            {iconEnd}
          </span>
        ) : null}
        {loading ? <span className="wd-btn__spinner" aria-hidden="true" /> : null}
        {loading ? <span className="wd-sr-only">{loadingLabel}</span> : null}
      </>
    );
    const reason =
      disabledReason && disabled ? (
        <span id={reasonId} className="wd-btn__reason">
          {disabledReason}
        </span>
      ) : null;

    if ('href' in props && props.href) {
      const { href, onClick, ...anchorRest } = rest as Omit<LinkButtonProps, keyof CommonProps | 'disabled'>;
      return (
        <>
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={isDisabled ? undefined : href}
            role={isDisabled ? 'link' : undefined}
            tabIndex={isDisabled ? -1 : undefined}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                return;
              }
              onClick?.(e);
            }}
            {...shared}
            {...anchorRest}
          >
            {body}
          </a>
          {reason}
        </>
      );
    }
    const {
      onClick,
      type = 'button',
      ...buttonRest
    } = rest as Omit<ButtonProps, keyof CommonProps | 'disabled'>;
    return (
      <>
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type={type}
          // Keep the button focusable when disabled so the reason stays discoverable by keyboard/AT (§13.4).
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }
            onClick?.(e);
          }}
          {...shared}
          {...buttonRest}
        >
          {body}
        </button>
        {reason}
      </>
    );
  },
);

export function ButtonGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('wd-btn-group', className)}>{children}</div>;
}
